@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.registrierung

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.browser.document
import kotlinx.coroutines.launch
import org.example.project.dashboard.DashboardTheme
import org.example.project.datenschutz.DatenschutzTexte
import org.example.project.datenschutz.SignaturDatenschutzOverlay
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.PasswordRequirementHint
import org.example.project.service.PasswordPolicy
import org.example.project.service.LoginService
import org.example.project.settings.UISettings
import org.example.project.utils.All
import org.example.project.utils.All.AllNames.names
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.backgroundColor
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.boxSizing
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.justifyContent
import org.jetbrains.compose.web.css.marginBottom
import org.jetbrains.compose.web.css.marginTop
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLCanvasElement

private const val NETZWERK_REG_CANVAS = "netzwerk-register-signatur-canvas"
private const val NETZWERK_REG_STEP_COUNT = 4

private fun netzwerkRegistrationLabel(fieldId: String, fallback: String = fieldId): String = names[fieldId] ?: fallback

private fun All.netzwerkHasCompleteRegisterAddress(): Boolean =
    strasse.isNotBlank() && hausnummer.isNotBlank() && plz.isNotBlank() && land.isNotBlank()

@Composable
fun NetzwerkRegistrierung(onLogin: () -> Unit, onRegistrationComplete: () -> Unit) {
    val scope = rememberCoroutineScope()
    var regStep by remember { mutableStateOf(1) }
    var einladendeFirma by remember { mutableStateOf("") }
    var selectedRoleId by remember { mutableStateOf("PRODUZENT") }
    var firmaAll by remember { mutableStateOf(All(land = "de", istnetzwerk = true)) }
    var memberAll by remember {
        mutableStateOf(
            All(
                id = "1",
                aktiv = false,
                zugangDesktop = true,
                datenschutzAkzeptiert = true,
                terminkalenderOnline = true,
                terminkalenderIntern = true,
                profilOnline = true,
                profilIntern = true
            )
        )
    }
    var regPasswort by remember { mutableStateOf("") }
    var regPasswortWdh by remember { mutableStateOf("") }
    var einladungsKey by remember { mutableStateOf("") }
    var einladungsKeyError by remember { mutableStateOf(false) }
    var firmaError by remember { mutableStateOf(false) }
    var emailError by remember { mutableStateOf(false) }
    var registrationErrorMsg by remember { mutableStateOf<String?>(null) }
    var regDatenschutzAkzeptiert by remember { mutableStateOf(false) }
    var showNetSignaturOverlay by remember { mutableStateOf(false) }
    var netCanvasGezeichnet by remember { mutableStateOf(false) }
    var netSignaturFehler by remember { mutableStateOf(false) }
    var loading by remember { mutableStateOf(false) }

    fun finalizeRegistration() {
        if (!firmaAll.netzwerkHasCompleteRegisterAddress()) {
            registrationErrorMsg = "Bitte Firma mit Straße, Hausnummer und PLZ vollständig ausfüllen."
            showNetSignaturOverlay = false
            return
        }
        if (!netCanvasGezeichnet || !regDatenschutzAkzeptiert) {
            netSignaturFehler = true
            return
        }
        val canvas = document.getElementById(NETZWERK_REG_CANVAS) as? HTMLCanvasElement
        if (canvas == null) {
            netSignaturFehler = true
            return
        }
        val signaturBase64 = canvas.toDataURL("image/png")
        scope.launch {
            try {
                loading = true
                registrationErrorMsg = null
                val registrierterBenutzer = DesktopRegistrierungApi.registerDesktopCompany(
                    firmaAll,
                    memberAll,
                    regPasswort,
                    signaturBase64,
                    einladungsKey
                )
                if (registrierterBenutzer.id.isBlank() || registrierterBenutzer.firmenId.isBlank()) {
                    registrationErrorMsg = "Registrierung konnte nicht gespeichert werden"
                    return@launch
                }
                showNetSignaturOverlay = false
                LoginService.login(registrierterBenutzer)
                onRegistrationComplete()
            } catch (error: Throwable) {
                registrationErrorMsg = "Fehler: ${error.message}"
            } finally {
                loading = false
            }
        }
    }

    if (showNetSignaturOverlay) {
        SignaturDatenschutzOverlay(
            canvasId = NETZWERK_REG_CANVAS,
            datenschutzText = DatenschutzTexte.NETZWERK,
            datenschutzLabel = "Ich habe die Datenschutzhinweise gelesen und stimme der Verarbeitung meiner Daten zu.",
            buttonLabel = if (loading) "..." else "Registrierung abschließen",
            signaturHeaderText = "Bitte unterzeichne im unteren Feld, dass du die Datenschutzerklärung gelesen hast, und schließe die Registrierung ab.",
            canvasHoehePx = 160,
            maxWidthPx = 560,
            overlayStyle = {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                alignItems(AlignItems.Center)
                backgroundColor(DashboardTheme.BACKGROUND_APP)
                property("background", DashboardTheme.BACKGROUND_APP_GRADIENT)
                property("z-index", "1000")
                property("font-family", "system-ui, Segoe UI, Arial")
            },
            cardStyle = {
                backgroundColor(DashboardTheme.SURFACE_DEEP)
                property("border", "1px solid #2a2d34")
                borderRadius(20.px)
                padding(40.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(20.px)
                boxSizing("border-box")
                property("box-shadow", "0 12px 48px rgba(0,0,0,0.8)")
                property("max-height", "90vh")
                property("overflow-y", "auto")
            },
            canvasGezeichnet = netCanvasGezeichnet,
            onCanvasGezeichnetChange = { netCanvasGezeichnet = it },
            datenschutzAkzeptiert = regDatenschutzAkzeptiert,
            onDatenschutzAkzeptiertChange = { regDatenschutzAkzeptiert = it },
            fehlerText = when {
                registrationErrorMsg != null -> registrationErrorMsg
                netSignaturFehler && !netCanvasGezeichnet -> "Bitte zeichne deine Unterschrift."
                netSignaturFehler -> "Bitte akzeptiere die Datenschutzerklärung."
                else -> null
            },
            onComplete = { if (!loading) finalizeRegistration() }
        )
    }

    Div({
        classes(NetzwerkLoginStyles.gifWrapper)
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            gap(0.px)
            width(100.percent)
        }
    }) {
        Div({
            style {
                width(850.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                alignItems(AlignItems.Stretch)
                gap(8.px)
            }
        }) {
            Div({
                classes(NetzwerkLoginStyles.registerFrame)
                style {
                    property("transform", "none")
                    width(100.percent)
                    height(550.px)
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    justifyContent(JustifyContent.Center)
                }
            }) {
                if (regStep in 2..4) {
                    Img(src = "images/back.svg", attrs = {
                        classes(NetzwerkLoginStyles.navIcon, NetzwerkLoginStyles.navLeft)
                        onClick { regStep-- }
                    })
                }

                when (regStep) {
                    1 -> {
                        H2 { Text("Einladungsschlüssel") }
                        P { Text("Bitte gib den Einladungsschlüssel ein, den du von deinem SNAP-Partner erhalten hast.") }
                        Div({ classes(NetzwerkLoginStyles.formGrid) }) {
                            NetzwerkInput(InputType.Text, einladungsKey, "Einladungsschlüssel", if (einladungsKeyError) "Einladungsschlüssel ist ungültig, abgelaufen oder bereits verwendet." else null, { einladungsKey = it; einladungsKeyError = false }, {}, loading)
                            LumaticaButton(
                                label = "Weiter",
                                onClick = { scope.launch {
                                    einladendeFirma = DesktopRegistrierungApi.invitationOwnerFirma(einladungsKey).orEmpty()
                                    einladungsKeyError = einladendeFirma.isBlank()
                                    if (!einladungsKeyError) { registrationErrorMsg = null; regStep = 2 }
                                } },
                                variant = LumaticaButtonVariant.Action,
                                enabled = einladungsKey.trim().split(".").size == 3 && !loading,
                                heightPx = 44
                            )
                        }
                    }
                    2 -> {
                        RoleIntroSelection(
                            selectedRoleId = selectedRoleId,
                            onSelect = { selectedRoleId = it },
                            onConfirm = {
                                firmaAll = firmaAll.copy(position = selectedRoleId)
                                regStep = 3
                            }
                        )
                    }
                    3 -> {
                        H2 { Text("Unternehmensdaten") }
                        P { Text("Bitte fülle alle Pflichtfelder aus") }
                        Div({ classes(NetzwerkLoginStyles.formGrid) }) {
                            NetzwerkInput(InputType.Text, firmaAll.firma, netzwerkRegistrationLabel("firmenname", "Firma"), null, {
                                firmaAll = firmaAll.copy(firma = it)
                                firmaError = false
                            }, {}, loading)
                            Div(attrs = { classes(NetzwerkLoginStyles.row) }) {
                                Div({ style { property("flex-grow", "1") } }) {
                                    NetzwerkInput(InputType.Text, firmaAll.strasse, "Straße", null, { firmaAll = firmaAll.copy(strasse = it) }, {}, loading)
                                }
                                Input(InputType.Text, attrs = {
                                    classes(NetzwerkLoginStyles.regInput)
                                    placeholder(netzwerkRegistrationLabel("hausnummer", "Nr."))
                                    style { width(80.px); property("flex-shrink", "0") }
                                    value(firmaAll.hausnummer)
                                    onInput { firmaAll = firmaAll.copy(hausnummer = it.value) }
                                })
                            }
                            Div(attrs = { classes(NetzwerkLoginStyles.row) }) {
                                Input(InputType.Text, attrs = {
                                    classes(NetzwerkLoginStyles.regInput)
                                    placeholder(netzwerkRegistrationLabel("plz", "PLZ"))
                                    style { width(120.px); property("flex-shrink", "0") }
                                    value(firmaAll.plz)
                                    onInput { firmaAll = firmaAll.copy(plz = it.value) }
                                })
                                Div({ style { property("flex-grow", "1") } }) {
                                    NetzwerkInput(InputType.Text, firmaAll.ort, "Ort", null, { firmaAll = firmaAll.copy(ort = it) }, {}, loading)
                                }
                            }
                        }
                        Div({ classes(NetzwerkLoginStyles.formGrid) }) {
                            LumaticaButton(
                                label = "Weiter",
                                onClick = {
                                    registrationErrorMsg = null
                                    regStep = 4
                                },
                                variant = LumaticaButtonVariant.Action,
                                enabled = firmaAll.firma.isNotBlank() && firmaAll.netzwerkHasCompleteRegisterAddress() && !firmaError,
                                heightPx = 44
                            )
                        }
                    }
                    4 -> {
                        H2 { Text("Sicherheit & Zugang") }
                        P { Text("Wer bist du und wie möchtest du dich anmelden?") }
                        Div({ classes(NetzwerkLoginStyles.formGrid) }) {
                            Div(attrs = { classes(NetzwerkLoginStyles.row) }) {
                                NetzwerkInput(InputType.Text, memberAll.vorname, netzwerkRegistrationLabel("vorname", "Vorname"), null, { memberAll = memberAll.copy(vorname = it) }, {}, loading)
                                NetzwerkInput(InputType.Text, memberAll.nachname, netzwerkRegistrationLabel("nachname", "Nachname"), null, { memberAll = memberAll.copy(nachname = it) }, {}, loading)
                            }
                            NetzwerkInput(InputType.Email, memberAll.email, netzwerkRegistrationLabel("email", "E-Mail"), if (emailError) "E-Mail existiert bereits!" else null, {
                                memberAll = memberAll.copy(email = it)
                                emailError = false
                                scope.launch {
                                    if (it.length > 5) emailError = DesktopRegistrierungApi.emailExists(it.lowercase())
                                }
                            }, {}, loading)
                            NetzwerkInput(InputType.Password, regPasswort, "Passwort", null, { regPasswort = it }, {}, loading)
                            PasswordRequirementHint(regPasswort)
                            NetzwerkInput(
                                InputType.Password,
                                regPasswortWdh,
                                "Passwort wiederholen",
                                if (regPasswortWdh.isNotEmpty() && regPasswort != regPasswortWdh) "Passwörter nicht identisch" else null,
                                { regPasswortWdh = it },
                                {},
                                loading
                            )
                        }
                        if (registrationErrorMsg != null) {
                            P({ classes(NetzwerkLoginStyles.errorParagraph) }) { Text(registrationErrorMsg ?: "") }
                        }
                        Div({ classes(NetzwerkLoginStyles.formGrid) }) {
                            LumaticaButton(
                                label = "Weiter",
                                onClick = {
                                    registrationErrorMsg = null
                                    netCanvasGezeichnet = false
                                    regDatenschutzAkzeptiert = false
                                    netSignaturFehler = false
                                    showNetSignaturOverlay = true
                                },
                                variant = LumaticaButtonVariant.Action,
                                enabled = regPasswort == regPasswortWdh && PasswordPolicy.isValid(regPasswort)
                                    && memberAll.vorname.isNotEmpty() && memberAll.nachname.isNotEmpty() && !emailError,
                                heightPx = 44
                            )
                        }
                    }
                }
                if (regStep == 1) Div({ classes(org.example.project.login.UnifiedAccessStyles.switchRow) }) {
                    Span { Text("Bereits registriert?") }
                    Span({
                        classes(org.example.project.login.UnifiedAccessStyles.switchAction)
                        onClick { onLogin() }
                    }) { Text("Anmelden") }
                }
            }
            NetzwerkRegistrationProgressTabs(regStep)
        }
    }
}

@Composable
private fun NetzwerkRegistrationProgressTabs(currentStep: Int) {
    Div({
        style {
            width(100.percent)
            display(DisplayStyle.Flex)
            gap(6.px)
            height(10.px)
            alignItems(AlignItems.Center)
        }
    }) {
        repeat(NETZWERK_REG_STEP_COUNT) { index ->
            Div({
                style {
                    property("flex", "1")
                    height(3.px)
                    backgroundColor(if (index + 1 == currentStep) UISettings.ACCENT else DashboardTheme.BORDER_LIGHT)
                }
            }) {}
        }
    }
}
